/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview SocialMedia component for managing company social media profiles.
 * This component handles social media URL inputs for LinkedIn, Facebook, Twitter/X,
 * YouTube, and Instagram with visibility toggle controls for public profile display.
 */

import React, { useState, useEffect, useRef } from "react";
import styles from "./SocialMedia.module.css";
import { useTranslation } from "react-i18next";
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/SocialMediaSection/LinkedInIcon.svg";
import { ReactComponent as FacebookIcon } from "../../../assets/icons/SocialMediaSection/FacebookIcon.svg";
import { ReactComponent as TwitterIcon } from "../../../assets/icons/SocialMediaSection/TwitterIcon.svg";
import { ReactComponent as YouTubeIcon } from "../../../assets/icons/SocialMediaSection/YouTubeIcon.svg";
import { ReactComponent as InstagramIcon } from "../../../assets/icons/SocialMediaSection/InstagramIcon.svg";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { IconButton } from "@mui/material";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { getCompanyFieldValue } from "../../../utils/common";

/** Translation key for "show in profile" toggle label */
const SHOW_IN_PROFILE_KEY = "components.contactInformation.showInProfile";

/** Color for active toggle state */
const TOGGLE_COLOR_ACTIVE = "#1d5ae8";

/** Color for inactive toggle state */
const TOGGLE_COLOR_INACTIVE = "#C7C8C8";

/**
 * Reusable input component for social media URL fields.
 * Renders a labeled input with an icon, validation state, and visibility toggle.
 *
 * @param {Object} props - Component props
 * @param {string} props.fieldName - Unique field identifier for validation
 * @param {string} props.value - Current input value (URL)
 * @param {boolean} props.showValue - Whether the field is visible on public profile
 * @param {React.ComponentType} props.Icon - SVG icon component for the social platform
 * @param {string} props.labelKey - Translation key for the input label
 * @param {string} props.placeholderKey - Translation key for the input placeholder
 * @param {Function} props.onValueChange - Callback when input value changes
 * @param {Function} props.onShowChange - Callback when visibility toggle changes
 * @param {Function} props.getFieldValidationState - Function to get validation state
 * @param {Function} props.getErrorMessage - Function to get validation error message
 * @param {Function} props.t - Translation function from react-i18next
 * @param {boolean} [props.isLast=false] - Whether this is the last input in the group
 * @returns {JSX.Element} The rendered social media input field
 */
const SocialMediaInput = ({
  fieldName,
  value,
  showValue,
  Icon,
  labelKey,
  placeholderKey,
  onValueChange,
  onShowChange,
  getFieldValidationState,
  getErrorMessage,
  t,
  isLast = false,
}) => {
  const hasError = getFieldValidationState(fieldName, value) === "error";
  const errorMessage = getErrorMessage(fieldName);
  const inputClassName = `${styles.input} ${styles.inputWithIcon} ${hasError ? styles.inputError : ""}`;
  const containerClassName = isLast
    ? `${styles.showInProfileContainer} ${styles.lastShowInProfileContainer}`
    : styles.showInProfileContainer;

  return (
    <div
      className={
        isLast
          ? `${styles.inputGroup} ${styles.twoLabelsGroup}`
          : styles.inputGroup
      }
    >
      <label className={styles.inputLabel}>{t(labelKey)}</label>
      <div className={styles.inputWrapper}>
        <Icon className={styles.inputIcon} />
        <input
          type="text"
          className={inputClassName}
          placeholder={t(placeholderKey)}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          maxLength={2083}
        />
      </div>
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
      <div className={containerClassName}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={onShowChange}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showValue ? TOGGLE_COLOR_ACTIVE : TOGGLE_COLOR_INACTIVE,
            },
          }}
        >
          {showValue ? <ToggleOnIcon color="primary" /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

/**
 * SocialMedia component for displaying and editing company social media profiles.
 * Manages URLs for LinkedIn, Facebook, Twitter/X, YouTube, and Instagram
 * with visibility toggles for public profile display.
 *
 * @param {Object} props - Component props
 * @param {Object} props.company - Company data object containing social media URLs
 * @param {Function} props.onFieldChange - Callback function triggered when a field value changes
 * @param {Function} props.onValidationChange - Callback function triggered when validation state changes
 * @param {number} [props.selectedProfile=0] - Currently selected company profile index for multi-profile support
 * @returns {JSX.Element} The rendered SocialMedia form section
 */
const SocialMedia = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const { t } = useTranslation();
  const prevHasErrorsRef = useRef(false);
  const isTablet = window.innerWidth <= 1023;

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    linkedIn: "",
    facebook: "",
    twitter: "",
    youtube: "",
    instagram: "",
  });

  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  const [linkedIn, setLinkedIn] = useState(
    getCompanyFieldValue(company, "linkedIn", selectedProfile, ""),
  );
  const [facebook, setFacebook] = useState(
    getCompanyFieldValue(company, "facebook", selectedProfile, ""),
  );
  const [twitter, setTwitter] = useState(
    getCompanyFieldValue(company, "twitter", selectedProfile, ""),
  );
  const [youtube, setYoutube] = useState(
    getCompanyFieldValue(company, "youtube", selectedProfile, ""),
  );
  const [instagram, setInstagram] = useState(
    getCompanyFieldValue(company, "instagram", selectedProfile, ""),
  );
  const [showLinkedIn, setShowLinkedIn] = useState(
    getCompanyFieldValue(company, "mostrarLinkedIn", selectedProfile, false),
  );
  const [showFacebook, setShowFacebook] = useState(
    getCompanyFieldValue(company, "mostrarFacebook", selectedProfile, false),
  );
  const [showTwitter, setShowTwitter] = useState(
    getCompanyFieldValue(company, "mostrarTwitter", selectedProfile, false),
  );
  const [showYoutube, setShowYoutube] = useState(
    getCompanyFieldValue(company, "mostrarYoutube", selectedProfile, false),
  );
  const [showInstagram, setShowInstagram] = useState(
    getCompanyFieldValue(company, "mostrarInstagram", selectedProfile, false),
  );

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const createValueChangeHandler = (fieldName, setter) => (value) => {
    setter(value);
    handleInputChange(fieldName, value);
    handleFieldChange(fieldName, value);
  };

  const createShowChangeHandler = (showFieldName, showValue, setter) => () => {
    const newValue = !showValue;
    setter(newValue);
    handleInputChange(showFieldName, newValue);
  };

  const tabletRowClassName = `${styles.inputRow} ${styles.twoInputsRow} ${isTablet ? styles.tabletInputRow : ""}`;
  const tabletGroupClassName = `${styles.inputGroup} ${styles.twoLabelsGroup} ${isTablet ? styles.tabletInstagramGroup : ""}`;

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.socialMedia.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <SocialMediaInput
          fieldName="linkedIn"
          value={linkedIn}
          showValue={showLinkedIn}
          Icon={LinkedInIcon}
          labelKey="components.socialMedia.linkedInLabel"
          placeholderKey="components.socialMedia.linkedInPlaceholder"
          onValueChange={createValueChangeHandler("linkedIn", setLinkedIn)}
          onShowChange={createShowChangeHandler(
            "mostrarLinkedIn",
            showLinkedIn,
            setShowLinkedIn,
          )}
          getFieldValidationState={getFieldValidationState}
          getErrorMessage={getErrorMessage}
          t={t}
        />
        <SocialMediaInput
          fieldName="facebook"
          value={facebook}
          showValue={showFacebook}
          Icon={FacebookIcon}
          labelKey="components.socialMedia.facebookLabel"
          placeholderKey="components.socialMedia.facebookPlaceholder"
          onValueChange={createValueChangeHandler("facebook", setFacebook)}
          onShowChange={createShowChangeHandler(
            "mostrarFacebook",
            showFacebook,
            setShowFacebook,
          )}
          getFieldValidationState={getFieldValidationState}
          getErrorMessage={getErrorMessage}
          t={t}
        />
        <SocialMediaInput
          fieldName="twitter"
          value={twitter}
          showValue={showTwitter}
          Icon={TwitterIcon}
          labelKey="components.socialMedia.twitterLabel"
          placeholderKey="components.socialMedia.twitterPlaceholder"
          onValueChange={createValueChangeHandler("twitter", setTwitter)}
          onShowChange={createShowChangeHandler(
            "mostrarTwitter",
            showTwitter,
            setShowTwitter,
          )}
          getFieldValidationState={getFieldValidationState}
          getErrorMessage={getErrorMessage}
          t={t}
        />
        <SocialMediaInput
          fieldName="youtube"
          value={youtube}
          showValue={showYoutube}
          Icon={YouTubeIcon}
          labelKey="components.socialMedia.youtubeLabel"
          placeholderKey="components.socialMedia.youtubePlaceholder"
          onValueChange={createValueChangeHandler("youtube", setYoutube)}
          onShowChange={createShowChangeHandler(
            "mostrarYoutube",
            showYoutube,
            setShowYoutube,
          )}
          getFieldValidationState={getFieldValidationState}
          getErrorMessage={getErrorMessage}
          t={t}
        />
      </div>
      <div className={tabletRowClassName}>
        <div className={tabletGroupClassName}>
          <SocialMediaInput
            fieldName="instagram"
            value={instagram}
            showValue={showInstagram}
            Icon={InstagramIcon}
            labelKey="components.socialMedia.instagramLabel"
            placeholderKey="components.socialMedia.instagramPlaceholder"
            onValueChange={createValueChangeHandler("instagram", setInstagram)}
            onShowChange={createShowChangeHandler(
              "mostrarInstagram",
              showInstagram,
              setShowInstagram,
            )}
            getFieldValidationState={getFieldValidationState}
            getErrorMessage={getErrorMessage}
            t={t}
            isLast={true}
          />
        </div>
      </div>
    </div>
  );
};

export default SocialMedia;
